import type { CollateralKey } from "@/data/fixtures";
import {
	COLLATERAL_KEYS,
	PROVIDER_LABELS,
	type ProviderQuoteRow,
} from "@/lib/oracle/price-providers";
import { publicUrl } from "@/lib/public-url";

export type KeeperProviderSnapshot = {
	updatedAt: number;
	network: string;
	source: string;
	quotes: Record<
		CollateralKey,
		Array<{
			provider: string;
			price: number | null;
			error: string | null;
		}>
	>;
};

export async function loadKeeperProviderSnapshots(): Promise<KeeperProviderSnapshot | null> {
	try {
		const res = await fetch(publicUrl("/data/oracle-provider-snapshots.json"), {
			cache: "no-cache",
		});
		if (!res.ok) return null;
		const data = (await res.json()) as KeeperProviderSnapshot;
		if (!data?.quotes) return null;
		return data;
	} catch {
		return null;
	}
}

function keeperRowsFromSnapshot(
	snapshot: KeeperProviderSnapshot,
): Record<CollateralKey, ProviderQuoteRow[]> {
	const out = Object.fromEntries(
		COLLATERAL_KEYS.map((asset) => [asset, [] as ProviderQuoteRow[]]),
	) as Record<CollateralKey, ProviderQuoteRow[]>;

	for (const asset of COLLATERAL_KEYS) {
		for (const row of snapshot.quotes[asset] ?? []) {
			out[asset].push({
				provider: row.provider,
				providerLabel: PROVIDER_LABELS[row.provider] ?? row.provider,
				asset,
				price: row.price,
				error: row.error,
			});
		}
	}
	return out;
}

/** Merge browser-fetched quotes with keeper snapshot (keeper fills gaps e.g. CMC). */
export function mergeProviderQuotes(
	browser: Record<CollateralKey, ProviderQuoteRow[]>,
	keeperSnapshot: KeeperProviderSnapshot | null,
): {
	quotes: Record<CollateralKey, ProviderQuoteRow[]>;
	keeperUpdatedAt: number | null;
} {
	if (!keeperSnapshot) {
		return { quotes: browser, keeperUpdatedAt: null };
	}

	const keeper = keeperRowsFromSnapshot(keeperSnapshot);
	const merged = Object.fromEntries(
		COLLATERAL_KEYS.map((asset) => {
			const byProvider = new Map<string, ProviderQuoteRow>();
			for (const row of keeper[asset] ?? []) {
				byProvider.set(row.provider, row);
			}
			for (const row of browser[asset] ?? []) {
				if (row.price != null) {
					byProvider.set(row.provider, row);
				} else if (!byProvider.has(row.provider)) {
					byProvider.set(row.provider, row);
				}
			}
			return [
				asset,
				[...byProvider.values()].sort((a, b) =>
					a.providerLabel.localeCompare(b.providerLabel),
				),
			];
		}),
	) as Record<CollateralKey, ProviderQuoteRow[]>;

	return {
		quotes: merged,
		keeperUpdatedAt: keeperSnapshot.updatedAt || null,
	};
}
